/**
 * Die App zoomt und dreht sich nicht als Ganzes.
 *
 * Gezoomt wird nur, wo es etwas zu vergrößern gibt: im Bild (Markierungen
 * setzen) und im Stammbaum (Überblick gegen Lesbarkeit). Beide bringen ihre
 * eigene Zoomleiste mit, halten die Geste selbst fest (`touch-action: none`
 * auf ihrer Fläche) und rechnen sie aus. Zoomt der Browser *zusätzlich* die
 * ganze Seite, verschiebt sich unter der Hand beides gleichzeitig – und
 * danach steht die Oberfläche schief im Fenster, mit abgeschnittener
 * Kopfzeile und einer Tab-Leiste außerhalb des Bildes.
 *
 * Drei Hebel sind nötig, weil jeder für sich Lücken lässt:
 *
 *   1. `user-scalable=no` im Viewport-Meta (`index.html`) – wirkt auf Android.
 *   2. `touch-action: pan-x pan-y` auf `html`/`body` (`index.css`) – nimmt
 *      Aufziehen und Doppeltippen überall dort, wo `touch-action` gilt.
 *   3. Diese Datei – für die beiden Fälle, die davon nicht erfasst werden:
 *      Safaris eigene `gesture*`-Ereignisse und das Zoomen mit Strg + Rad
 *      (auf dem Trackpad ist das die Aufziehgeste).
 *
 * **Was bewusst bleibt:** die Zoomstufe des Browsers über Tastatur und Menü
 * (⌘/Strg +, −, 0). Sie ist die Einstellung, auf die angewiesen ist, wer
 * schlecht sieht – und anders als die Geste löst sie das Fenster nicht aus
 * seiner Verankerung. Abfangen ließe sie sich ohnehin nicht zuverlässig.
 */
export function lockViewport(): void {
  /**
   * Safari kennt `gesturestart`/`gesturechange`/`gestureend` – eine eigene
   * Familie neben den Zeigerereignissen, die kein `touch-action` erreicht.
   * Ohne sie zoomt iOS trotz aller Meta-Angaben weiter die Seite.
   *
   * Auch über Bild und Stammbaum: dort läuft die Zwei-Finger-Geste über
   * `pointer*`, und die läuft weiter, gleich was hier abgesagt wird.
   */
  const stopGesture = (event: Event) => event.preventDefault();
  document.addEventListener('gesturestart', stopGesture, { passive: false });
  document.addEventListener('gesturechange', stopGesture, { passive: false });
  document.addEventListener('gestureend', stopGesture, { passive: false });

  /**
   * Strg + Rad ist am Schreibtisch dieselbe Geste: auf dem Trackpad meldet der
   * Browser das Aufziehen mit zwei Fingern als Rad-Ereignis mit `ctrlKey`.
   *
   * In der Erfassungsphase (`capture`) und ausdrücklich nicht passiv, sonst
   * bliebe das Absagen wirkungslos. Bild und Stammbaum zoomen bei Strg + Rad
   * selbst; ihre Behandlung läuft danach ganz normal weiter, denn abgesagt ist
   * hier nur die Voreinstellung des Browsers, nicht die Weitergabe.
   */
  window.addEventListener(
    'wheel',
    (event) => {
      if (event.ctrlKey) event.preventDefault();
    },
    { passive: false, capture: true },
  );

  /**
   * Nicht drehen.
   *
   * Der verlässliche Weg ist `"orientation": "portrait"` im Manifest – die
   * installierte App startet damit im Hochformat und bleibt darin. Der Riegel
   * hier greift zusätzlich, wenn das Manifest zur Laufzeit nicht durchschlägt.
   *
   * Nur in der installierten App: im Browser-Tab lehnen alle Browser das
   * Festhalten ab (zu Recht – ein Tab bestimmt nicht über das Gerät), und der
   * abgewiesene Versuch würde nur eine Fehlermeldung in die Konsole schreiben.
   * Auf iOS gibt es die Schnittstelle gar nicht; dort bleibt es beim Manifest.
   */
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;

  if (standalone) {
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (to: string) => Promise<void>;
    };
    // Beides absichern: manche Browser werfen sofort, andere lehnen die
    // Zusage ab. Beides ist hier kein Fehler, sondern die Auskunft „geht auf
    // diesem Gerät nicht" – und dann bleibt es beim Manifest.
    try {
      void orientation?.lock?.('portrait').catch(() => {});
    } catch {
      /* still */
    }
  }
}
